import React, { useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSearchQuery, useSearchFilters, useSearchPage, useSearchContext } from '../contexts/SearchContext';
import { usePushIndexUiState } from '../hooks/usePushIndexUiState';
import { navigate } from 'vike/client/router';
import { getIndexName } from '../constants';
import { generateUrlFromIndexUiState } from '../utils/search-router/url-handling';
import { formatFilterLabel } from '../utils/facets/format-filter-label';
import { footerMessages } from '../utils/footer/footer.messages';
import { SrpAlertCta } from './job-alerts/SrpAlertCta';
import { Link } from './Link';
import { MagnifyingGlass, X, Briefcase, MapPin, Lightbulb, ArrowRight, ArrowClockwise, ChartLine, BookOpen } from '@phosphor-icons/react';
import { Msg } from '../i18n-review/client/Msg';

// iOS26 style utility classes
const iosFontStack = "font-[system-ui,-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif]";

/**
 * Curated fallback suggestions for the "Popular searches" section. A true zero-hit query also
 * returns zero facet counts, so a facet-derived suggestion list would always be empty here —
 * these evergreen chips (not derived from the current, empty result set) are what actually
 * gives the user somewhere to go. Values match the exact strings stored in the location facet
 * (see location-list-facetable-migration.ts).
 */
const POPULAR_CITY_CHIPS: Array<{ message: typeof footerMessages.jobsInHelsinki; value: string }> = [
  { message: footerMessages.jobsInHelsinki, value: 'Helsinki, Finland' },
  { message: footerMessages.jobsInTampere, value: 'Tampere, Finland' },
  { message: footerMessages.jobsInEspoo, value: 'Espoo, Finland' },
  { message: footerMessages.jobsInTurku, value: 'Turku, Finland' },
  { message: footerMessages.jobsInOulu, value: 'Oulu, Finland' },
];

interface NoResultsProps {
  query?: string;
  /** True when this is standing in for a search-backend error, not a genuine zero-hit search. */
  isBackendUnavailable?: boolean;
}

/**
 * No-results state for the SRP. Deliberately single-purpose: the one CTA we want users to take
 * is signing up for email alerts on this search (SrpAlertCta) — everything else here is either
 * informational (search tips) or a low-key way back into browsing (search-parameters box,
 * popular searches), not a competing call to action.
 */
export function NoResults({ query: queryProp, isBackendUnavailable = false }: NoResultsProps) {
  const intl = useIntl();
  const { query: contextQuery } = useSearchQuery();
  const { filters } = useSearchFilters();
  const { page } = useSearchPage();
  const { pushIndexUiState } = usePushIndexUiState();
  const { refetch } = useSearchContext();

  // Use query from prop or context
  const query = queryProp || contextQuery;

  // Check if we're on a page > 1
  const isNotFirstPage = page > 0;

  // Parse filters
  const filtersObj = useMemo(() => {
    if (!filters) return {};
    if (typeof filters === 'string') {
      try {
        return JSON.parse(filters) as Record<string, string[]>;
      } catch {
        return {};
      }
    }
    return filters as Record<string, string[]>;
  }, [filters]);

  // Check if any filters are applied
  const hasFilters = useMemo(() => {
    return Object.keys(filtersObj).length > 0;
  }, [filtersObj]);

  // Handler for applying a filter (popular-search chips)
  const handleFilterClick = (attribute: string, value: string) => {
    pushIndexUiState({
      query: '',
      refinementList: { [attribute]: [value] },
      page: 0,
    });
  };

  // Handler for removing a specific filter
  const handleRemoveFilter = (attribute: string, value: string) => {
    const currentValues = filtersObj[attribute] || [];
    const newValues = currentValues.filter(v => v !== value);
    const newFilters = { ...filtersObj };
    if (newValues.length === 0) {
      delete newFilters[attribute];
    } else {
      newFilters[attribute] = newValues;
    }
    pushIndexUiState({
      query: query || '',
      refinementList: newFilters,
      page: 0,
    });
  };

  // Handler for clearing the query
  const handleClearQuery = () => {
    pushIndexUiState({
      query: '',
      refinementList: filtersObj,
      page: 0,
    });
  };

  // Handler for retrying the search after a backend error (the one action that makes sense
  // during an outage — clearing filters/query wouldn't fix it).
  const handleRetry = () => {
    refetch();
  };

  // Handler for going to first page
  const handleGoToFirstPage = () => {
    pushIndexUiState({
      query: query || '',
      refinementList: filtersObj,
      page: 0,
    });
  };

  // Handler for clearing all search filters and query
  const handleClearSearch = async () => {
    try {
      const indexName = getIndexName(intl.locale);
      const clearedState = {
        query: '',
        refinementList: {},
        page: 0,
        sort: 'relevance' as const,
      };

      // Update state immediately
      pushIndexUiState(clearedState);

      // Also navigate using Vike's router for proper routing
      const url = generateUrlFromIndexUiState(clearedState, indexName);
      await navigate(url);
    } catch (error) {
      // Silently handle navigation errors
    }
  };

  // Get active filter labels for display
  const activeFilterLabels = useMemo(() => {
    const labels: Array<{ attribute: string; value: string; label: string }> = [];
    Object.entries(filtersObj).forEach(([attribute, values]) => {
      if (Array.isArray(values)) {
        values.forEach(value => {
          labels.push({ attribute, value, label: formatFilterLabel(attribute, value) });
        });
      }
    });
    return labels;
  }, [filtersObj]);

  return (
    <div className={`min-h-[50vh] w-full flex flex-col items-center justify-center py-6 px-4 ${iosFontStack} bg-transparent`}>
      <div className="w-full max-w-4xl mx-auto">
        {/* Main Card */}
        <div className="backdrop-blur-xl bg-white/95 dark:bg-[#1F1509]/95 rounded-3xl shadow-2xl dark:shadow-none border border-[#DDD5C8]/60 dark:border-[#4A3520]/60 p-6 sm:p-8">
          {/* Header Section */}
          <div className="flex flex-col items-center mb-6">
            <div className="w-16 h-16 rounded-full bg-[#C8DDCE] dark:bg-[#1A2E22] flex items-center justify-center mb-4">
              <MagnifyingGlass className="w-8 h-8 text-[#3B5E45] dark:text-[#98CFA8]" />
            </div>
            <h2 className="text-2xl font-bold text-[#1E1A14] dark:text-[#F2EADF] mb-2 text-center">
              {isBackendUnavailable ? (
                <Msg
                  defaultMessage="We're having trouble loading jobs"
                  description="Title shown when the search backend is unreachable, not a genuine zero-hit search"
                  id="search.noresults.unavailable.title"
                />
              ) : (
                <Msg
                  defaultMessage="No results found"
                  description="No results message"
                  id="search.noresults.title"
                />
              )}
            </h2>
            <p className="text-[#7A6E5F] dark:text-[#B8A48C] text-sm text-center max-w-md mb-4">
              {isBackendUnavailable ? (
                <Msg
                  defaultMessage="Our job search is temporarily unavailable. Try refreshing the page, or check out these pages in the meantime."
                  description="Description shown when the search backend is unreachable, not a genuine zero-hit search"
                  id="search.noresults.unavailable.description"
                />
              ) : (
                <Msg
                  defaultMessage="Try adjusting your search or filters to find more opportunities."
                  description="Helpful message for no results"
                  id="search.noresults.description"
                />
              )}
            </p>
            {isBackendUnavailable && (
              <button
                onClick={handleRetry}
                type="button"
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-semibold text-white bg-[#1a5632] hover:bg-[#14432a] cursor-pointer"
              >
                <ArrowClockwise className="w-4 h-4" />
                <Msg defaultMessage="Try again" description="Button to retry the search after a backend error" id="search.noresults.unavailable.retry" />
              </button>
            )}
          </div>

          {/* Search Parameters Section - Shows what caused no results (not shown during a backend
              error: clearing filters/query wouldn't fix an outage, so offering to isn't honest) */}
          {!isBackendUnavailable && (query || hasFilters || isNotFirstPage) && (
            <div className="mb-6 p-4 rounded-xl bg-[#F8F5F0] dark:bg-[#170F07]/50 border border-[#DDD5C8] dark:border-[#4A3520]">
              <div className="flex items-center gap-2 mb-3">
                <X className="w-4 h-4 text-[#7A6E5F] dark:text-[#B8A48C]" />
                <h3 className="text-sm font-semibold text-[#1E1A14] dark:text-[#F2EADF]">
                  <Msg
                    defaultMessage="Your Search Parameters"
                    description="Label for search parameters section"
                    id="search.noresults.searchParameters"
                  />
                </h3>
              </div>

              <div className="space-y-3">
                {/* Query */}
                {query && (
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <MagnifyingGlass className="w-4 h-4 text-[#7A6E5F] dark:text-[#B8A48C]" />
                      <span className="text-xs font-medium text-[#3D3529] dark:text-[#C4A882]">
                        <Msg
                          defaultMessage="Query:"
                          description="Label for search query"
                          id="search.noresults.queryLabel"
                        />
                      </span>
                      <span className="text-sm font-semibold text-[#1E1A14] dark:text-[#F2EADF]">"{query}"</span>
                    </div>
                    <button
                      onClick={handleClearQuery}
                      className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium bg-[#DDD5C8] dark:bg-[#2A1D0F] text-[#3D3529] dark:text-[#C4A882] hover:bg-[#EDE3D4] dark:hover:bg-[#342415] transition-colors cursor-pointer group"
                      type="button"
                    >
                      <Msg
                        defaultMessage="Remove"
                        description="Button to remove query"
                        id="search.noresults.removeQuery"
                      />
                      <X className="w-3 h-3 group-hover:text-red-500 transition-colors" />
                    </button>
                  </div>
                )}

                {/* Filters */}
                {hasFilters && activeFilterLabels.length > 0 && (
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <Briefcase className="w-4 h-4 text-[#7A6E5F] dark:text-[#B8A48C]" />
                      <span className="text-xs font-medium text-[#3D3529] dark:text-[#C4A882]">
                        <Msg
                          defaultMessage="Filters:"
                          description="Label for filters"
                          id="search.noresults.filtersLabel"
                        />
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {activeFilterLabels.map(({ attribute, value, label }) => (
                        <button
                          key={`${attribute}-${value}`}
                          onClick={() => handleRemoveFilter(attribute, value)}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-[#F2EADF] dark:bg-[#2A1D0F] text-[#3D3529] dark:text-[#C4A882] hover:bg-[#EDE3D4] dark:hover:bg-[#342415] transition-colors cursor-pointer group"
                          type="button"
                        >
                          <span>{label}</span>
                          <X className="w-3 h-3 group-hover:text-red-500 transition-colors" />
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Page Number */}
                {isNotFirstPage && (
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-medium text-[#3D3529] dark:text-[#C4A882]">
                        <Msg
                          defaultMessage="Page:"
                          description="Label for page number"
                          id="search.noresults.pageLabel"
                        />
                      </span>
                      <span className="text-sm font-semibold text-[#1E1A14] dark:text-[#F2EADF]">
                        {page + 1}
                      </span>
                    </div>
                    <button
                      onClick={handleGoToFirstPage}
                      className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium bg-[#DDD5C8] dark:bg-[#2A1D0F] text-[#3D3529] dark:text-[#C4A882] hover:bg-[#EDE3D4] dark:hover:bg-[#342415] transition-colors cursor-pointer group"
                      type="button"
                    >
                      <Msg
                        defaultMessage="Go to page 1"
                        description="Button to go to first page"
                        id="search.noresults.goToFirstPage"
                      />
                      <ArrowRight className="w-3 h-3" />
                    </button>
                  </div>
                )}

                {/* Clear All Button — the one way back into browsing; replaces the old separate Browse All Jobs / Clear Search buttons */}
                {(query || hasFilters || isNotFirstPage) && (
                  <div className="pt-2 border-t border-[#DDD5C8] dark:border-[#4A3520]">
                    <button
                      onClick={handleClearSearch}
                      className="text-xs text-[#3B5E45] dark:text-[#98CFA8] hover:text-[#2A4533] dark:hover:text-[#B6D4BC] font-medium flex items-center gap-1 transition-colors cursor-pointer"
                      type="button"
                    >
                      <Msg
                        defaultMessage="Clear all search parameters"
                        description="Button to clear all search parameters"
                        id="search.noresults.clearAllParameters"
                      />
                      <ArrowRight className="w-3 h-3" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Primary CTA: get email alerts for this search. The one thing we want users to do here. */}
          <div className="mb-6">
            <SrpAlertCta hasActiveSearch />
          </div>

          {isBackendUnavailable ? (
            /* During an outage, link to pages backed by DynamoDB/static content rather than the
               search index — a location/remoteness chip would just re-run the same broken search. */
            <div className="mb-2">
              <h3 className="text-base font-semibold text-[#1E1A14] dark:text-[#F2EADF] mb-3 flex items-center gap-2">
                <Lightbulb className="w-5 h-5 text-[#3B5E45] dark:text-[#98CFA8]" />
                <Msg
                  defaultMessage="In the meantime"
                  description="Heading above outage-safe links (salary hub, resources) shown instead of popular searches during a search-backend outage"
                  id="search.noresults.unavailable.linksLabel"
                />
              </h3>
              <div className="flex flex-wrap gap-1.5">
                <Link
                  href="/salary"
                  locale={intl.locale as 'en' | 'fi' | 'sv'}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white dark:bg-[#1F1509] border border-[#DDD5C8] dark:border-[#4A3520] text-[#3D3529] dark:text-[#C4A882] hover:bg-[#C8DDCE]/35 dark:hover:bg-[#1A2E22]/45 hover:border-[#3B5E45]/35 dark:hover:border-[#98CFA8]/30 hover:text-[#2A4533] dark:hover:text-[#B6D4BC] transition-all duration-200 text-sm"
                >
                  <ChartLine className="w-3.5 h-3.5 text-[#3B5E45] dark:text-[#98CFA8]" />
                  <span className="font-medium">
                    <Msg defaultMessage="Explore salary data" description="Link to the salary hub shown during a search-backend outage" id="search.noresults.unavailable.exploreSalary" />
                  </span>
                </Link>
                <Link
                  href="/resources"
                  locale={intl.locale as 'en' | 'fi' | 'sv'}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white dark:bg-[#1F1509] border border-[#DDD5C8] dark:border-[#4A3520] text-[#3D3529] dark:text-[#C4A882] hover:bg-[#C8DDCE]/35 dark:hover:bg-[#1A2E22]/45 hover:border-[#3B5E45]/35 dark:hover:border-[#98CFA8]/30 hover:text-[#2A4533] dark:hover:text-[#B6D4BC] transition-all duration-200 text-sm"
                >
                  <BookOpen className="w-3.5 h-3.5 text-[#3B5E45] dark:text-[#98CFA8]" />
                  <span className="font-medium">
                    <Msg defaultMessage="Read career resources" description="Link to the resources hub shown during a search-backend outage" id="search.noresults.unavailable.exploreResources" />
                  </span>
                </Link>
              </div>
            </div>
          ) : (
            <>
              {/* Popular Searches Section - curated, always available (a facet-derived list would always be empty for a genuine zero-hit query) */}
              <div className="mb-6">
                <h3 className="text-base font-semibold text-[#1E1A14] dark:text-[#F2EADF] mb-3 flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-[#3B5E45] dark:text-[#98CFA8]" />
                  <Msg
                    defaultMessage="Popular searches"
                    description="Heading for curated popular search suggestions (cities, remote, hybrid) shown on the no-results page"
                    id="search.noresults.popularSearches"
                  />
                </h3>
                <div className="flex flex-wrap gap-1.5">
                  {POPULAR_CITY_CHIPS.map(({ message, value }) => (
                    <button
                      key={value}
                      onClick={() => handleFilterClick('location', value)}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white dark:bg-[#1F1509] border border-[#DDD5C8] dark:border-[#4A3520] text-[#3D3529] dark:text-[#C4A882] hover:bg-[#C8DDCE]/35 dark:hover:bg-[#1A2E22]/45 hover:border-[#3B5E45]/35 dark:hover:border-[#98CFA8]/30 hover:text-[#2A4533] dark:hover:text-[#B6D4BC] transition-all duration-200 cursor-pointer text-sm"
                      type="button"
                    >
                      <MapPin className="w-3.5 h-3.5 text-[#3B5E45] dark:text-[#98CFA8]" />
                      <span className="font-medium"><Msg {...message} /></span>
                    </button>
                  ))}
                  <button
                    onClick={() => handleFilterClick('remoteness', 'Remote')}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white dark:bg-[#1F1509] border border-[#DDD5C8] dark:border-[#4A3520] text-[#3D3529] dark:text-[#C4A882] hover:bg-[#C8DDCE]/35 dark:hover:bg-[#1A2E22]/45 hover:border-[#3B5E45]/35 dark:hover:border-[#98CFA8]/30 hover:text-[#2A4533] dark:hover:text-[#B6D4BC] transition-all duration-200 cursor-pointer text-sm"
                    type="button"
                  >
                    <Briefcase className="w-3.5 h-3.5 text-[#3B5E45] dark:text-[#98CFA8]" />
                    <span className="font-medium"><Msg {...footerMessages.remoteJobs} /></span>
                  </button>
                  <button
                    onClick={() => handleFilterClick('remoteness', 'Hybrid')}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white dark:bg-[#1F1509] border border-[#DDD5C8] dark:border-[#4A3520] text-[#3D3529] dark:text-[#C4A882] hover:bg-[#C8DDCE]/35 dark:hover:bg-[#1A2E22]/45 hover:border-[#3B5E45]/35 dark:hover:border-[#98CFA8]/30 hover:text-[#2A4533] dark:hover:text-[#B6D4BC] transition-all duration-200 cursor-pointer text-sm"
                    type="button"
                  >
                    <Briefcase className="w-3.5 h-3.5 text-[#3B5E45] dark:text-[#98CFA8]" />
                    <span className="font-medium">
                      <Msg
                        defaultMessage="Hybrid jobs"
                        description="Chip suggesting hybrid remoteness jobs as a popular search"
                        id="search.noresults.hybridJobs"
                      />
                    </span>
                  </button>
                </div>
              </div>

              {/* Search Tips Section */}
              <div className="p-4 rounded-xl bg-[#C8DDCE]/40 dark:bg-[#1A2E22]/50 border border-[#3B5E45]/25 dark:border-[#4A3520]">
                <div className="flex items-start gap-2">
                  <Lightbulb className="w-5 h-5 text-[#3B5E45] dark:text-[#98CFA8] flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <h3 className="text-sm font-semibold text-[#1E1A14] dark:text-[#F2EADF] mb-1.5">
                      <Msg
                        defaultMessage="Search Tips"
                        description="Heading for search tips"
                        id="search.noresults.searchTips"
                      />
                    </h3>
                    <ul className="space-y-1 text-xs text-[#3D3529] dark:text-[#C4A882]">
                      <li className="flex items-start gap-1.5">
                        <span className="text-[#3B5E45] dark:text-[#98CFA8] mt-0.5">•</span>
                        <span>
                          <Msg
                            defaultMessage="Try using broader search terms or remove some filters"
                            description="Search tip 1"
                            id="search.noresults.tip1"
                          />
                        </span>
                      </li>
                      <li className="flex items-start gap-1.5">
                        <span className="text-[#3B5E45] dark:text-[#98CFA8] mt-0.5">•</span>
                        <span>
                          <Msg
                            defaultMessage="Check your spelling or try alternative keywords"
                            description="Search tip 2"
                            id="search.noresults.tip2"
                          />
                        </span>
                      </li>
                      <li className="flex items-start gap-1.5">
                        <span className="text-[#3B5E45] dark:text-[#98CFA8] mt-0.5">•</span>
                        <span>
                          <Msg
                            defaultMessage="Browse popular categories above to discover new opportunities"
                            description="Search tip 3"
                            id="search.noresults.tip3"
                          />
                        </span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
